/**
 * Lace & Luna — IP/Markets country → currency, plus language redirect.
 * UK/GB IP → GBP. European IP → that country (EUR markets). Never map English → GBP.
 * Manual language/country clicks are stored in localStorage; ?country= is respected.
 */
(function () {
  const MANUAL_KEY = 'lace_locale_manual';
  const MANUAL_COUNTRY_KEY = 'lace_country_manual';
  const LEGACY_KEY = 'lace_locale_choice';
  const SESSION_KEY = 'lace_geo_attempt';

  const COUNTRY_LOCALE = {
    ES: 'es', MX: 'es', AR: 'es', CO: 'es', CL: 'es', PE: 'es', VE: 'es',
    EC: 'es', GT: 'es', CU: 'es', BO: 'es', DO: 'es', HN: 'es', PY: 'es',
    SV: 'es', NI: 'es', CR: 'es', PA: 'es', UY: 'es', PR: 'es', GQ: 'es', AD: 'es',
    PT: 'pt-PT', AO: 'pt-PT', MZ: 'pt-PT', CV: 'pt-PT', GW: 'pt-PT', ST: 'pt-PT', TL: 'pt-PT',
    BR: 'pt-PT',
  };
  const LOCALE_PREFIX = {
    es: '/es',
    'pt-PT': '/pt',
  };

  const config = window.LaceLocalization || {};
  const available = (config.availableLocales || []).map(String);
  const currentLocale = config.locale || 'en';

  if (!available.length || document.documentElement.classList.contains('shopify-design-mode')) return;

  function storageGet(store, key) {
    try {
      return store.getItem(key);
    } catch (err) {
      return null;
    }
  }

  function storageSet(store, key, value) {
    try {
      store.setItem(key, value);
    } catch (err) {
      /* Safari private / blocked storage */
    }
  }

  function storageRemove(store, key) {
    try {
      store.removeItem(key);
    } catch (err) {
      /* ignore */
    }
  }

  function normalizeLocale(code) {
    if (!code) return null;
    const value = String(code).trim();
    const match = available.find((locale) => locale.toLowerCase() === value.toLowerCase());
    if (match) return match;
    const mapped = COUNTRY_LOCALE[value.toUpperCase()];
    if (!mapped) return null;
    return available.find((locale) => locale.toLowerCase() === mapped.toLowerCase()) || null;
  }

  function normalizeCountry(code) {
    if (!code) return '';
    const value = String(code).trim().toUpperCase();
    if (value === 'UK') return 'GB';
    return value;
  }

  function getManualLocale() {
    const stored = storageGet(localStorage, MANUAL_KEY) || storageGet(localStorage, LEGACY_KEY);
    if (!stored) return null;

    const locale = normalizeLocale(stored);
    if (!locale) {
      storageRemove(localStorage, MANUAL_KEY);
      storageRemove(localStorage, LEGACY_KEY);
      return null;
    }

    if (stored !== locale) storageSet(localStorage, MANUAL_KEY, locale);
    if (storageGet(localStorage, LEGACY_KEY)) storageRemove(localStorage, LEGACY_KEY);
    return locale;
  }

  function getManualCountry() {
    return normalizeCountry(storageGet(localStorage, MANUAL_COUNTRY_KEY) || '');
  }

  function urlCountry() {
    try {
      return normalizeCountry(new URLSearchParams(window.location.search).get('country') || '');
    } catch (err) {
      return '';
    }
  }

  function pathWithoutLocalePrefix() {
    const path = window.location.pathname;
    for (const prefix of Object.values(LOCALE_PREFIX)) {
      if (path === prefix || path.startsWith(prefix + '/')) {
        return path.slice(prefix.length) || '/';
      }
    }
    return path;
  }

  function localeFromPath() {
    const path = window.location.pathname;
    for (const [locale, prefix] of Object.entries(LOCALE_PREFIX)) {
      if (path === prefix || path.startsWith(prefix + '/')) return locale;
    }
    return normalizeLocale(currentLocale) || 'en';
  }

  function postLocalization(locale, country) {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/localization';
    form.style.display = 'none';

    const fields = {
      form_type: 'localization',
      utf8: '✓',
      _method: 'put',
      return_to: window.location.pathname + window.location.search,
      locale_code: locale,
    };
    if (country) fields.country_code = country;

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
  }

  function applyLocale(locale, country) {
    const targetLocale = normalizeLocale(locale);
    if (!targetLocale) return;

    const activeLocale = localeFromPath();
    if (activeLocale.toLowerCase() === targetLocale.toLowerCase()) return;

    const prefix = LOCALE_PREFIX[targetLocale];
    if (prefix) {
      const target = prefix + pathWithoutLocalePrefix() + window.location.search;
      if (window.location.pathname + window.location.search !== target) {
        storageSet(sessionStorage, SESSION_KEY, '1');
        window.location.replace(target);
      }
      return;
    }

    storageSet(sessionStorage, SESSION_KEY, '1');
    postLocalization(targetLocale, country);
  }

  function resolveTarget(country, suggestedLanguage) {
    const fromSuggestion = normalizeLocale(suggestedLanguage);
    if (fromSuggestion) return fromSuggestion;
    return normalizeLocale(COUNTRY_LOCALE[(country || '').toUpperCase()]);
  }

  function bindManualPicks() {
    document.querySelectorAll('form[action*="localization"] a[data-value]').forEach((link) => {
      link.addEventListener('click', () => {
        const value = link.getAttribute('data-value') || '';
        if (/^[A-Z]{2}$/.test(value)) {
          storageSet(localStorage, MANUAL_COUNTRY_KEY, value);
          return;
        }
        const locale = normalizeLocale(value);
        if (locale) storageSet(localStorage, MANUAL_KEY, locale);
      });
    });
  }

  function runLanguageRedirect(country) {
    const manualLocale = getManualLocale();
    if (manualLocale) {
      const prefix = LOCALE_PREFIX[manualLocale];
      if (prefix) {
        const currentPath = window.location.pathname;
        if (currentPath !== prefix && !currentPath.startsWith(prefix + '/')) {
          const target = prefix + pathWithoutLocalePrefix() + window.location.search;
          if (currentPath + window.location.search !== target) {
            storageSet(sessionStorage, SESSION_KEY, '1');
            window.location.replace(target);
          }
        }
      }
      return;
    }

    if (storageGet(sessionStorage, SESSION_KEY)) return;

    const target = resolveTarget(country);
    if (target) applyLocale(target, country);
  }

  function countryFromSuggestions(data) {
    const suggestions = data && data.suggestions;
    if (Array.isArray(suggestions)) {
      for (let i = 0; i < suggestions.length; i += 1) {
        const handle = suggestions[i] && suggestions[i].parts && suggestions[i].parts.country && suggestions[i].parts.country.handle;
        if (handle) return normalizeCountry(handle);
      }
    }
    const detected = data && data.detected_values && data.detected_values.country && data.detected_values.country.handle;
    return normalizeCountry(detected || '');
  }

  function syncCountryFromIp() {
    const explicit = urlCountry() || getManualCountry();
    if (explicit) return Promise.resolve(false);
    if (storageGet(sessionStorage, SESSION_KEY)) return Promise.resolve(false);

    const currentCountry = normalizeCountry(config.country || (window.Shopify && window.Shopify.country) || '');
    const cartCurrency = String(config.cartCurrency || '').toUpperCase();
    const countryCurrency = String(config.countryCurrency || '').toUpperCase();
    const activeLocale = localeFromPath();

    if (currentCountry === 'GB' && cartCurrency && cartCurrency !== 'GBP') {
      storageSet(sessionStorage, SESSION_KEY, '1');
      postLocalization(currentLocale || activeLocale, 'GB');
      return Promise.resolve(true);
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const exclude = encodeURIComponent((window.Shopify && window.Shopify.country) || currentCountry || '');
    const url = root + 'browsing_context_suggestions.json?country[enabled]=true&country[exclude]=' + exclude;

    return fetch(url, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data) return false;
        const ipCountry = countryFromSuggestions(data);
        if (!ipCountry || ipCountry === currentCountry) return false;
        if (currentCountry === ipCountry && (!countryCurrency || !cartCurrency || cartCurrency === countryCurrency)) {
          return false;
        }
        storageSet(sessionStorage, SESSION_KEY, '1');
        postLocalization(currentLocale || activeLocale, ipCountry);
        return true;
      })
      .catch(() => false);
  }

  bindManualPicks();

  const countryOverride = urlCountry() || getManualCountry();
  if (countryOverride) {
    runLanguageRedirect(countryOverride);
    return;
  }

  syncCountryFromIp().then((didPost) => {
    if (didPost) return;
    runLanguageRedirect(normalizeCountry(config.country || (window.Shopify && window.Shopify.country) || ''));
  });
})();
